import Box from '@mui/material/Box';
import { iconButtonClasses } from '@mui/material/IconButton';
import type { Breakpoint } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';

import CompaniesPopover from '@components/CompaniesPopover';
import { ItemQrCodeScanner } from '@components/ItemQrCodeScanner';
import type { NavSectionProps } from '@components/NavSection';
import ReceivedConnectPopover from '@components/ReceivedConnect/';
import { RecipientQrCode } from '@components/RecipientQrCode';
import { useSettings } from '@hooks/useSettings';
import { useToogle } from '@hooks/useToogle';
import { useUserPermissions } from '@hooks/useUserPermissions';
import { Roles } from '@rdTypes/shared';
import merge from 'lodash/fp/merge';
import { useSettingsContext } from 'src/components/settings';

import type { HeaderSectionProps, LayoutSectionProps, MainSectionProps } from '@src/layouts/core';
import { HeaderSection, LayoutSection, MainSection } from '@src/layouts/core';

import { AccountPopover } from '../components/account-popover';
import { MenuButton } from '../components/menu-button';
import { useNavData } from '../config-navigation';
import { layoutClasses } from '../core/classes';
import { dashboardLayoutVars, dashboardNavColorVars } from './css-vars';
import { NavMobile } from './nav-mobile';
import { NavVertical } from './nav-vertical';

type LayoutBaseProps = Pick<LayoutSectionProps, 'sx' | 'children' | 'cssVars'>;

export type DashboardLayoutProps = LayoutBaseProps & {
  layoutQuery?: Breakpoint;
  slotProps?: {
    header?: HeaderSectionProps;
    nav?: {
      data?: NavSectionProps['data'];
    };
    main?: MainSectionProps;
  };
};

export function DashboardLayout({ sx, cssVars, children, slotProps, layoutQuery = 'lg' }: DashboardLayoutProps) {
  const theme = useTheme();
  const { isAllowed } = useUserPermissions();
  const labelPrintingSettings: Record<string, boolean> = useSettings(['LabelPrintingEnabled']);

  const settings = useSettingsContext();

  const navVars = dashboardNavColorVars(theme, settings.state.navColor, settings.state.navLayout);

  const { value: open, setFalse: onClose, setTrue: onOpen } = useToogle();

  const navConfig = useNavData();

  const isNavMini = settings.state.navLayout === 'mini';
  const isNavHorizontal = settings.state.navLayout === 'horizontal';
  const isNavVertical = isNavMini || settings.state.navLayout === 'vertical';

  const renderHeader = () => {
    const headerSlotProps: HeaderSectionProps['slotProps'] = {
      container: {
        maxWidth: false,
        sx: {
          ...(isNavVertical && { px: { [layoutQuery]: 5 } }),
          ...(isNavHorizontal && {
            bgcolor: 'var(--layout-nav-bg)',
            height: { [layoutQuery]: 'var(--layout-nav-horizontal-height)' },
            [`& .${iconButtonClasses.root}`]: { color: 'var(--layout-nav-text-secondary-color)' },
          }),
        },
      },
    };

    const headerSlots: HeaderSectionProps['slots'] = {
      leftArea: (
        <>
          {/** @slot Nav mobile */}
          <MenuButton
            onClick={onOpen}
            sx={{ mr: 1, ml: -1, [theme.breakpoints.up(layoutQuery)]: { display: 'none' } }}
          />
          <NavMobile data={navConfig} open={open} onClose={onClose} cssVars={navVars.section} />
          {isAllowed([Roles.Mailroom, Roles.Admin]) && labelPrintingSettings.LabelPrintingEnabled && (
            <ItemQrCodeScanner />
          )}
        </>
      ),
      rightArea: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0, sm: 0.75 } }}>
          {isAllowed([Roles.Mailroom, Roles.Admin]) && labelPrintingSettings.LabelPrintingEnabled && (
            <ReceivedConnectPopover />
          )}
          {isAllowed([Roles.KTAdmin]) && <CompaniesPopover />}
          {isAllowed([Roles.Recipient]) && <RecipientQrCode />}
          <AccountPopover />
        </Box>
      ),
    };

    return (
      <HeaderSection
        layoutQuery={layoutQuery}
        disableElevation={isNavVertical}
        {...slotProps?.header}
        slots={{ ...headerSlots, ...slotProps?.header?.slots }}
        slotProps={merge(headerSlotProps, slotProps?.header?.slotProps ?? {})}
        sx={slotProps?.header?.sx}
      />
    );
  };

  const renderSidebar = () => (
    <NavVertical
      data={navConfig}
      isNavMini={isNavMini}
      layoutQuery={layoutQuery}
      cssVars={navVars.section}
      onToggleNav={() => settings.setField('navLayout', settings.state.navLayout === 'vertical' ? 'mini' : 'vertical')}
    />
  );

  const renderMain = () => <MainSection {...slotProps?.main}>{children}</MainSection>;

  return (
    <LayoutSection
      headerSection={renderHeader()}
      sidebarSection={renderSidebar()}
      cssVars={{ ...dashboardLayoutVars(theme), ...navVars.layout, ...cssVars }}
      sx={[
        {
          [`& .${layoutClasses.sidebarContainer}`]: {
            [theme.breakpoints.up(layoutQuery)]: {
              pl: isNavMini ? 'var(--layout-nav-mini-width)' : 'var(--layout-nav-vertical-width)',
              transition: theme.transitions.create(['padding-left'], {
                easing: 'var(--layout-transition-easing)',
                duration: 'var(--layout-transition-duration)',
              }),
            },
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {renderMain()}
    </LayoutSection>
  );
}
